// @ts-strict-ignore
import ShopDetails from './ShopDetails'
import ZoomButtons from './ZoomButtons'
import MarkerClusterer from '@google/markerclustererplus'
import { MultipleItemResponse } from '@lib/kontent'
import { RolexStorePageV72024 } from '@lib/kontent/models/RolexStorePageV72024'
import { mapStyles } from '@lib/map-styles'
import GoogleMapReact from 'google-map-react'
import { FC, useRef, useState } from 'react'

type Props = MultipleItemResponse<RolexStorePageV72024>

const RetailerMap: FC<Props> = ({ items }) => {
  const [activeLocation, setActiveLocation] = useState<null | number>(null)
  const [markers, setMarkers] = useState([])
  const mapEl = useRef<any>()
  const mapsEl = useRef<any>()

  const handleGoogleMapsApiLoaded = (map, maps) => {
    // Make map & maps accessible to other functions by adding it to state
    mapEl.current = map
    mapsEl.current = maps
    const createdMarkers =
      items &&
      items.map((retailer) => {
        const marker = new maps.Marker({
          position: {
            lat: retailer.elements.latitude.value,
            lng: retailer.elements.longitude.value,
          },
          title: retailer.elements.name.value,
          icon: '/images/rolex/map_location.png',
        })
        return marker
      })
    createdMarkers.forEach((marker, index) => {
      mapsEl.current.event.addListener(marker, 'click', () => showInfo(index, mapEl, marker))
    })
    // Make markers accessible to other functions by adding it to state
    setMarkers(createdMarkers)
    new MarkerClusterer(map, createdMarkers, {
      minimumClusterSize: 2,
      styles: [
        {
          height: 30,
          url: '/images/rolex/map_cluster.png',
          width: 30,
          textColor: 'transparent',
        },
      ],
    })
  }

  const mapOptions = (maps) => ({
    styles: mapStyles,
    fullscreenControl: false,
    zoomControl: false,
    zoomControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
    },
  })

  const zoomIn = () => {
    if (mapEl.current) {
      const curZoom = mapEl.current.getZoom()
      curZoom !== 21 && mapEl.current.setZoom(curZoom + 1)
    }
  }

  const zoomOut = () => {
    if (mapEl.current) {
      const curZoom = mapEl.current.getZoom()
      curZoom !== 0 && mapEl.current.setZoom(curZoom - 1)
    }
  }

  // Reset markers and active location when ShopDetails window is closed
  const showInfo = (index: number, map, marker) => {
    map.current.panTo(marker.getPosition())
    markers.forEach((marker, i) => {
      i === index
        ? marker.setIcon('/images/rolex/map_location_selected.png')
        : marker.setIcon('/images/rolex/map_location.png')
    })
    setActiveLocation(index)
  }

  // Reset all marker icons and active location when ShopDetails window is closed
  const hideInfo = () => {
    markers.forEach((marker) => {
      marker.setIcon('/images/rolex/map_location.png')
    })
    setActiveLocation(null)
  }

  return (
    <div className="relative h-auto min-h-[460px] overflow-hidden pb-[70%] md:h-0 lg:min-h-[700px]">
      <div className="absolute right-[25px] top-[25px]"></div>
      <ZoomButtons onZoomIn={() => zoomIn()} onZoomOut={() => zoomOut()} />
      <GoogleMapReact
        style={{ position: 'absolute', height: '100%', width: '100%' }}
        bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY }}
        yesIWantToUseGoogleMapApiInternals
        options={mapOptions}
        defaultCenter={{
          lat: 39.5997132,
          lng: 47.316306,
        }}
        defaultZoom={1}
        onGoogleApiLoaded={({ map, maps }) => handleGoogleMapsApiLoaded(map, maps)}
      />
      <ShopDetails
        isVisible={activeLocation !== null}
        onClose={hideInfo}
        retailer={activeLocation !== null && items[activeLocation].elements}
      />
    </div>
  )
}

export default RetailerMap
